<script>
import { TheMask } from "vue-the-mask"
import Icon from "@/core/components/Icon"

export default {
  name: "OrganizationContacts",

  components: {
    Icon,
    TheMask,
  },

  props: {
    user: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isEdit: false,
      loading: false,
      errors: {},
      contacts: [],
    }
  },

  mounted() {
    this.fetchContacts()
  },

  methods: {
    toggleEdit() {
      this.errors = {}

      if (this.isEdit) {
        this.isEdit = false
        this.fetchContacts()
        return
      }

      this.isEdit = true

      if (!this.contacts.length) {
        this.addContact()
      }
    },

    normalizeContacts(data = []) {
      return (data || []).map(item => ({
        id: item.id || null,
        name: item.name || "",
        phone: item.phone || "",
        email: item.email || "",
        description: item.description || "",
      }))
    },

    async fetchContacts() {
      this.loading = true

      try {
        const response = await this.$http.get(
          `/api/registry/organization/${this.user.id}/contacts`
        )

        this.contacts = this.normalizeContacts(response.data.contacts)
      } catch (error) {
        this.contacts = []
      } finally {
        this.loading = false
      }
    },

    addContact() {
      this.contacts.push({
        id: null,
        name: "",
        phone: "",
        email: "",
        description: "",
      })
    },

    removeContact(index) {
      if (index > -1 && this.contacts.length > 1) {
        this.contacts.splice(index, 1)
      }
    },

    buildPayload() {
      return {
        contacts: this.contacts.map(contact => ({
          ...(contact.id ? { id: contact.id } : {}),
          name: (contact.name || "").trim(),
          phone: (contact.phone || "").replace(/\D/g, ""),
          email: (contact.email || "").trim(),
          description: (contact.description || "").trim(),
        })),
      }
    },

    async save() {
      this.$Progress.start()
      this.errors = {}

      try {
        const response = await this.$http.put(
          `/api/registry/organization/${this.user.id}/contacts`,
          this.buildPayload()
        )

        if (response.data.success) {
          this.isEdit = false
          await this.fetchContacts()
        }

        this.$Progress.finish()
      } catch (error) {
        this.errors = error.response?.data?.errors || {}
        this.$Progress.fail()
      }
    },

    formatPhone(value) {
      const digits = (value || "").replace(/\D/g, "")

      if (digits.length === 11) {
        return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3")
      }

      if (digits.length === 10) {
        return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3")
      }

      return value || ""
    },
  },
}
</script>

<template>
  <div class="w-full bg-gray-light">
    <div :class="'mx-2 my-4 modal-edit' + (isEdit ? ' edit-shadow' : '')">
      <div class="flex flex-wrap justify-between align-baseline mb-1">
        <h1 class="text-green-base uppercase">Contatos</h1>

        <div
          v-if="editable"
          class="form-new-green"
          @click="toggleEdit"
        >
          <div>{{ isEdit ? "Fechar" : "Editar" }}</div>
        </div>
      </div>

      <div class="border-t-2 border-green-base pt-8">

        <div v-if="loading" class="py-5 px-2 text-xs">
          Carregando contatos...
        </div>

        <div v-else-if="!isEdit" class="py-5 text-xs">
          <div v-if="!contacts.length" class="px-2">
            Nenhum contato cadastrado.
          </div>

          <div
            v-for="(contact, index) in contacts"
            :key="contact.id || index"
            class="w-full px-2 mb-6"
          >
            <div class="w-full well p-4">
              <div class="flex flex-wrap">
                <div class="w-full md:w-1/4 pr-2 mb-2">
                  <span class="form-label mb-0">Nome</span>
                  <div>{{ contact.name }}</div>
                </div>

                <div class="w-full md:w-1/4 pr-2 mb-2">
                  <span class="form-label mb-0">Telefone</span>
                  <div>{{ formatPhone(contact.phone) }}</div>
                </div>

                <div class="w-full md:w-1/4 pr-2 mb-2 break-all">
                  <span class="form-label mb-0">E-mail</span>
                  <div>{{ contact.email }}</div>
                </div>

                <div class="w-full md:w-1/4 mb-2">
                  <span class="form-label mb-0">Observação</span>
                  <div>{{ contact.description || "-" }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="py-5 text-xs">
          <div
            v-for="(contact, index) in contacts"
            :key="contact.id || index"
            class="w-full px-2 mb-6"
          >
            <div class="w-full well p-4">
              <div class="flex justify-end mb-2">
                <a
                  v-if="contacts.length > 1"
                  title="Remover contato"
                  @click="removeContact(index)"
                >
                  <Icon
                    icon="close"
                    class="h-5 fill-current text-red-base focus:outline-none rounded-full bg-white shadow hover:bg-red-base hover:text-white active:bg-red-800"
                  />
                </a>
              </div>

              <div class="flex flex-wrap">
                <div class="w-full md:w-1/4 pr-2 mb-3">
                  <span class="form-label mb-0">Nome</span>
                  <input
                    v-model="contact.name"
                    class="form-input rounded py-2 h-auto"
                    type="text"
                  >
                  <span
                    v-for="error in errors[`contacts.${index}.name`]"
                    :key="error"
                    class="text-red-500 text-xs font-bold pb-1 block"
                  >
                    {{ error }}
                  </span>
                </div>

                <div class="w-full md:w-1/4 pr-2 mb-3">
                  <span class="form-label mb-0">Telefone</span>
                  <TheMask
                    v-model="contact.phone"
                    class="form-input rounded py-2 h-auto"
                    :mask="['(##) ####-####', '(##) #####-####']"
                  />
                  <span
                    v-for="error in errors[`contacts.${index}.phone`]"
                    :key="error"
                    class="text-red-500 text-xs font-bold pb-1 block"
                  >
                    {{ error }}
                  </span>
                </div>

                <div class="w-full md:w-1/4 pr-2 mb-3">
                  <span class="form-label mb-0">E-mail</span>
                  <input
                    v-model="contact.email"
                    class="form-input rounded py-2 h-auto"
                    type="email"
                  >
                  <span
                    v-for="error in errors[`contacts.${index}.email`]"
                    :key="error"
                    class="text-red-500 text-xs font-bold pb-1 block"
                  >
                    {{ error }}
                  </span>
                </div>

                <div class="w-full md:w-1/4 mb-3">
                  <span class="form-label mb-0">Observação</span>
                  <input
                    v-model="contact.description"
                    class="form-input rounded py-2 h-auto"
                    type="text"
                  >
                  <span
                    v-for="error in errors[`contacts.${index}.description`]"
                    :key="error"
                    class="text-red-500 text-xs font-bold pb-1 block"
                  >
                    {{ error }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="px-2">
            <button
              class="form-green py-1 w-full sm:w-40 mb-6"
              @click="addContact"
            >
              Novo contato
            </button>
          </div>

          <div class="flex justify-center sm:justify-start mt-4 px-2 w-full">
            <button class="form-submit w-full sm:w-auto" @click="save">
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>